import React, { useContext, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import DatePicker from "react-multi-date-picker";
import { numberSuffix } from '../../helper/stringMethods';
import { addDay } from '../../helper/dateHelper';
import toast from 'react-hot-toast';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import DContext from '../../context/DContext';
import ContentLoader from '../loader/ContentLoader';
import { inventoriesService } from '../../services/inventories.service';
import { airlinesService } from '../../services/airlines.service';
import { airportsService } from '../../services/airports.service';
import { flightsService } from '../../services/flights.service';
import { suppliersService } from '../../services/suppliers.service';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';

const GRAD = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

const toArray = (rd) => (Array.isArray(rd) ? rd : rd?.data || []);

// Keeps the airport fields the inventory payload needs, plus the react-select value/label
const toAirportOption = (a) => ({
  ...a,
  value: a.id,
  label: `${a.city_name}, ${a.airport_name} (${a.airport_code})`,
});

// Auto-format typed digits as HH:MM
const formatTime = (raw) => {
  let value = raw.replace(/\D/g, "");
  if (value.length > 2) value = value.slice(0, 2) + ":" + value.slice(2, 4);
  return value.slice(0, 5);
};

const AddInventory = () => {
  const navigate = useNavigate();

  const { authState } = useContext(DContext);
  const { user, userProfile } = authState;

  const today = new Date();
  const [monthsToShow, setMonthsToShow] = useState(window.innerWidth < 768 ? 1 : 2);
  const [dateValue, setDateValue] = useState([]);
  const [nextDayArrivalSelectedValue, setNextDayArrivalSelectedValue] = useState("0");
  const [flightStops, setFlightStops] = useState(0);
  const [airlinesDropdown, setAirlinesDropdown] = useState([]);
  const [stopsData, setStopsData] = useState([]);
  const [loading, setLoading] = useState(false);

  const inventoryStateInitial = {
    trip_type: "O",
    flight_stops: "",
    airline_id: "",
    flight_number: "",

    departure_airport_id: "",
    departure_airport_name: "",
    departure_airport_code: "",
    departure_airport_city: "",
    departure_time: "",
    departure_terminal: "",

    arrival_airport_id: "",
    arrival_airport_name: "",
    arrival_airport_code: "",
    arrival_airport_city: "",
    arrival_time: "",
    arrival_terminal: "",

    rbd: "",
    baggage_policy: "15",
    fare_rule: "non_refundable_non_changable",
    fare_code: "",
    supplier_type: "self",
    supplier_info: user?.name,
    supplier_id: userProfile?.id || null,
    hr: ""
  }

  const [inventoryInfo, setInventoryInfo] = useState(inventoryStateInitial);
  const [pnrInfo, setPnrInfo] = useState([]);
  const [flightInfo, setFlightInfo] = useState({ flightCode: "", flightNumber: "" })
  const [selectedAirline, setSelectedAirline] = useState(null);
  const [depAirport, setDepAirport] = useState(null);
  const [arrAirport, setArrAirport] = useState(null);
  const [airportData, setAirportData] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  useEffect(() => {
    loadAirlines();
    loadAirports().then(setAirportData);
  }, []);

  useEffect(() => {
    const handleResize = () => setMonthsToShow(window.innerWidth < 768 ? 1 : 2);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // one blank stop per selected stop count
  useEffect(() => {
    if (!isNaN(flightStops)) {
      setStopsData(Array.from({ length: parseInt(flightStops) }, (_, i) => ({
        departure_airport: null,
        arrival_airport: null,
        stop_number: i + 1,
        departure_time: '',
        arrival_time: '',
        departure_terminal: '',
        arrival_terminal: ''
      })));
    }
  }, [flightStops]);

  // one PNR block per selected travel date
  useEffect(() => {
    if (Array.isArray(dateValue)) {
      // keep what was already entered for dates that are still selected
      setPnrInfo((prev) => dateValue.map((singleDate) => {
        const departure_date = singleDate.toString();
        const arrival_date = nextDayArrivalSelectedValue == "0" ? departure_date : addDay(departure_date, 1);
        const existing = prev.find((p) => p.departure_date === departure_date);
        return existing ? { ...existing, arrival_date } : {
          departure_date,
          arrival_date,
          pnr: "",
          seat_quantity: 0,
          infant_amount: 0,
          basic_amount: 0,
          yq_amount: 0,
          yr_amount: 0,
          ot_amount: 0,
          total_amount: 0,
          markup1: 0,
          markup2: 0
        };
      }));
    }
  }, [dateValue, nextDayArrivalSelectedValue]);

  const getLastStop = () => {
    if (stopsData?.length > 0) {
      return stopsData[stopsData?.length - 1]?.arrival_airport;
    }
    return { city_name: inventoryInfo?.arrival_airport_city, airport_code: inventoryInfo?.arrival_airport_code };
  }

  const handleStopChange = (index, field, value) => {
    const updatedStops = [...stopsData];
    if (["departure_time", "arrival_time"].includes(field)) value = formatTime(value);
    updatedStops[index][field] = value;
    setStopsData(updatedStops);
  };

  const handlePnrChange = (index, field, value) => {
    const updatedPnr = [...pnrInfo];
    const amountFields = ['basic_amount', 'yq_amount', 'yr_amount', 'ot_amount'];
    if (amountFields.includes(field)) {
      updatedPnr[index][field] = Math.max(0, parseFloat(value) || 0);
      const { basic_amount, yq_amount, yr_amount, ot_amount } = updatedPnr[index];
      updatedPnr[index].total_amount = parseFloat((basic_amount + yq_amount + yr_amount + ot_amount).toFixed(2));
    } else {
      updatedPnr[index][field] = ['seat_quantity', 'infant_amount', 'markup1', 'markup2'].includes(field) ? Math.max(0, Number(value) || 0) : value;
    }
    setPnrInfo(updatedPnr);
  };

  const handleFlightStop = (e) => {
    setFlightStops(e.target.value);
    setInventoryInfo((prev) => ({ ...prev, flight_stops: e.target.value }));
  }

  const handleFlightNumber = (e) => {
    setFlightInfo((prev) => ({ ...prev, flightNumber: e.target.value }));
    setInventoryInfo((prev) => ({ ...prev, flight_number: e.target.value }));
  }

  const handleAirlineChange = (selectedOption) => {
    setSelectedAirline(selectedOption);
    setFlightInfo((prev) => ({ ...prev, flightCode: selectedOption?.airline_code || "" }));
    setInventoryInfo((prev) => ({ ...prev, airline_id: selectedOption?.value || "" }));
  };

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon })
  }

  // Airline dropdown: airlines > list (status = active)
  const loadAirlines = async () => {
    try {
      const res = await airlinesService.list({ page: 1, status: "active" });
      if (res?.success) {
        setAirlinesDropdown(toArray(res.resData).map(a => ({ ...a, value: a.id, label: `${a.airline_name} (${a.airline_code})` })));
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    }
  }

  // Airport dropdowns: airports > list (status = active), searched on the server
  const loadAirports = async (search = "") => {
    try {
      const res = await airportsService.list({ page: 1, limit: 50, status: "active", ...(search ? { search } : {}) });
      if (res?.success) return toArray(res.resData).map(toAirportOption);
      toast.error(res?.message);
      return [];
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
      return [];
    }
  }

  // Supplier dropdown: suppliers > list (status = active)
  const loadSuppliers = async (search = "") => {
    try {
      const res = await suppliersService.list({ offset: 0, limit: 20, status: "active", ...(search ? { search } : {}) });
      if (res?.success) {
        return (res.resData?.data || []).map(s => ({ value: s.id, label: s.agency_name, agency_name: s.agency_name }));
      }
      toast.error(res?.message);
      return [];
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
      return [];
    }
  }

  const handleDepAirportChange = (o) => {
    setDepAirport(o);
    setInventoryInfo((prev) => ({
      ...prev,
      departure_airport_id: o?.id || "",
      departure_airport_name: o?.airport_name || "",
      departure_airport_code: o?.airport_code || "",
      departure_airport_city: o?.city_name || "",
    }));
  };

  const handleArrAirportChange = (o) => {
    setArrAirport(o);
    setInventoryInfo((prev) => ({
      ...prev,
      arrival_airport_id: o?.id || "",
      arrival_airport_name: o?.airport_name || "",
      arrival_airport_code: o?.airport_code || "",
      arrival_airport_city: o?.city_name || "",
    }));
  };

  // Fills airline, airports, times and terminals from a saved flight (flights > list)
  const getFlightInfo = async () => {
    if (!flightInfo.flightCode || !flightInfo.flightNumber) {
      toast.error("Select an airline and enter a flight number");
      return;
    }
    try {
      const res = await flightsService.list({
        page: 1, limit: 1,
        flight_code: flightInfo.flightCode,
        flight_number: flightInfo.flightNumber,
      });
      if (!res?.success) {
        toast.error(res?.message);
        return;
      }
      const flight = res.resData?.flights?.[0];
      if (!flight) {
        toast.error("Flight info not found");
        return;
      }
      toast.success(res.message);
      const dep = flight.departureAirport;
      const arr = flight.arrivalAirport;
      setDepAirport(toAirportOption(dep));
      setArrAirport(toAirportOption(arr));
      setInventoryInfo((prev) => ({
        ...prev,
        flight_number: flight.flight_number,
        departure_airport_id: dep?.id,
        departure_airport_name: dep?.airport_name,
        departure_airport_code: dep?.airport_code,
        departure_airport_city: dep?.city_name,
        departure_time: (flight.departure_time || "").slice(0, 5),
        departure_terminal: flight.departure_terminal || "",
        arrival_airport_id: arr?.id,
        arrival_airport_name: arr?.airport_name,
        arrival_airport_code: arr?.airport_code,
        arrival_airport_city: arr?.city_name,
        arrival_time: (flight.arrival_time || "").slice(0, 5),
        arrival_terminal: flight.arrival_terminal || "",
      }));
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    }
  }

  const handleSelectChangeSupplier = (selectedOption) => {
    setSelectedSupplier(selectedOption);
    if (selectedOption) {
      setInventoryInfo((prev) => ({ ...prev, supplier_info: selectedOption?.agency_name, supplier_id: selectedOption?.value }))
    } else {
      setInventoryInfo((prev) => ({ ...prev, supplier_info: "", supplier_id: null }))
    }
  };

  const handleInventory = (e) => {
    const { name, value } = e.target;
    if (name == "supplier_type") {
      if (value == "self") {
        setSelectedSupplier(null);
        setInventoryInfo({ ...inventoryInfo, [name]: value, supplier_info: user?.name, supplier_id: userProfile?.id || null });
      } else {
        setInventoryInfo({ ...inventoryInfo, [name]: value, supplier_info: "", supplier_id: null });
      }
    } else if (["departure_time", "arrival_time"].includes(name)) {
      setInventoryInfo({ ...inventoryInfo, [name]: formatTime(value) });
    } else {
      setInventoryInfo({ ...inventoryInfo, [name]: value });
    }
  }

  const validate = () => {
    if (!inventoryInfo.airline_id) return "Airline is required";
    if (!inventoryInfo.flight_number) return "Flight number is required";
    if (!inventoryInfo.departure_airport_id) return "Departure airport is required";
    if (!inventoryInfo.arrival_airport_id) return "Arrival airport is required";
    if (!/^\d{2}:\d{2}$/.test(inventoryInfo.departure_time)) return "Enter departure time as HH:MM";
    if (!/^\d{2}:\d{2}$/.test(inventoryInfo.arrival_time)) return "Enter arrival time as HH:MM";
    if (dateValue.length < 1) return "Select at least one travel date";
    if (inventoryInfo.supplier_type === "other" && !inventoryInfo.supplier_id) return "Select a supplier";
    for (const stop of stopsData) {
      if (!stop.departure_airport || !stop.arrival_airport) return `${numberSuffix(stop.stop_number)} stop airports are required`;
    }
    for (const pnr of pnrInfo) {
      if (!pnr.pnr.trim()) return `PNR is required for ${pnr.departure_date}`;
      if (!(Number(pnr.seat_quantity) > 0)) return `Seat quantity is required for ${pnr.departure_date}`;
    }
    return null;
  }

  const handleInventorySubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const confirm = await withReactContent(Swal).fire({
      title: "Save inventory?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, save",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);
      const res = await inventoriesService.create({
        flight_stop_count: flightStops,
        sector_details: inventoryInfo,
        flight_stops_data: stopsData,
        pnr_details: pnrInfo,
      });
      if (res?.success) {
        await withReactContent(Swal).fire({ title: "Success", text: res.message, icon: "success" });
        navigate("/inventory/list");
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  if (loading) return (<ContentLoader loaderType="circleSpin" />)

  const labelStyle = { fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: "#6c757d", marginBottom: 6 };
  const inputStyle = { borderRadius: 8, border: "1px solid #dfe3ec", fontSize: 13, height: 40, background: "#fff" };
  const readOnlyStyle = { ...inputStyle, background: "#eef0f8", color: "#4a5568" };
  const selectStyles = {
    control: (base, state) => ({
      ...base, borderRadius: 8, border: state.isFocused ? "1px solid #667eea" : "1px solid #dfe3ec", minHeight: 40,
      fontSize: 13, boxShadow: state.isFocused ? "0 0 0 3px rgba(102,126,234,.15)" : "none", "&:hover": { borderColor: "#667eea" }
    }),
    option: (base, state) => ({
      ...base, fontSize: 13,
      background: state.isSelected ? "#667eea" : state.isFocused ? "#eef0ff" : "#fff",
      color: state.isSelected ? "#fff" : "#1f2a44",
    }),
    valueContainer: (base) => ({ ...base, padding: "0 10px" }),
    placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  };
  const sectionCard = { background: "#fff", borderRadius: 12, border: "1px solid #e8ebf7", padding: 20, marginBottom: 16 };
  const sectionTitle = (icon, text) => (
    <h6 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ fontSize: 14, color: "#4b3f8f" }}>
      <i className={"bi " + icon} style={{ color: "#667eea" }}></i> {text}
    </h6>
  );
  const field = (label, required, node, cls = "col-12 col-md-6") => (
    <div className={cls}>
      <div style={labelStyle}>{label}{required && <span className="text-danger"> *</span>}</div>
      {node}
    </div>
  );
  const textInput = (props) => <input type="text" className="form-control" style={inputStyle} {...props} />;
  const stopBox = { marginTop: 16, background: "#f8f9fe", borderRadius: 10, border: "1px solid #eef0f8", padding: "16px 18px" };

  const STATUS_FARE_RULES = [{ value: "non_refundable_non_changable", label: "Non Refundable, Non Changable" }];
  const SUPPLIER_TYPES = [{ value: "self", label: "Self" }, { value: "other", label: "Other" }];
  const TRIP_TYPES = [{ value: "O", label: "One Way" }, { value: "R", label: "Round Trip" }];
  const STOP_OPTIONS = [{ value: 0, label: "Direct" }, { value: 1, label: "1 Stop" }, { value: 2, label: "2 Stop" }];

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "#1a202c", fontSize: 20 }}>{"Add New Inventory"}</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#8a9ab5" }}>
              <Link to="/" style={{ color: "#8a9ab5" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <Link to="/inventory/list" style={{ color: "#8a9ab5" }}>Inventory</Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>Add Inventory</span>
            </nav>
          </div>
          <Link to="/inventory/list" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
            style={{ background: "#4f3df5", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
            <i className="bi bi-list-ul"></i> Inventory List
          </Link>
        </div>

        <form onSubmit={handleInventorySubmit}>

            {/* Flight Configuration */}
            <div style={sectionCard}>
              {sectionTitle("bi-signpost-2", "Flight Configuration")}
              <div className="row g-3">
                {field("Trip Type", false,
                  <Select
                    options={TRIP_TYPES}
                    value={TRIP_TYPES.find(o => o.value === inventoryInfo.trip_type) || null}
                    onChange={(o) => setInventoryInfo({ ...inventoryInfo, trip_type: o ? o.value : "O" })}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Flight Stops", false,
                  <Select
                    options={STOP_OPTIONS}
                    value={STOP_OPTIONS.find(o => o.value == flightStops) || null}
                    onChange={(o) => handleFlightStop({ target: { value: o ? o.value : 0 } })}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
              </div>
            </div>

            {/* Flight Detail */}
            <div style={sectionCard}>
              {sectionTitle("bi-airplane", "Flight Detail")}
              <div className="row g-3">
                {field("Travel Date", true,
                  <>
                    <DatePicker
                      multiple
                      value={dateValue}
                      numberOfMonths={monthsToShow}
                      onChange={setDateValue}
                      inputClass="custom-input-calendar form-control"
                      dateSeparator=" , "
                      minDate={today}
                      placeholder="Select travel dates"
                      style={{ height: 40, borderRadius: 8, width: "100%", fontSize: 13 }}
                    />
                    {dateValue.length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {dateValue.map((d, i) => (
                          <span key={d.toString()} className="d-inline-flex align-items-center gap-2"
                            style={{ background: "#eef0ff", color: "#4b3f8f", border: "1px solid #dfe3f7", borderRadius: 20, padding: "4px 6px 4px 12px", fontSize: 12, fontWeight: 600 }}>
                            <i className="bi bi-calendar3"></i> {d.toString()}
                            <button type="button" title="Remove date" onClick={() => setDateValue(dateValue.filter((_, idx) => idx !== i))}
                              style={{ border: "none", background: "#fff", color: "#d93025", width: 20, height: 20, borderRadius: "50%", padding: 0, lineHeight: 1 }}>
                              <i className="bi bi-x" style={{ fontSize: 14 }}></i>
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </>,
                  "col-12"
                )}
                {field("Airline", true,
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ flex: 1 }}>
                      <Select
                        options={airlinesDropdown}
                        value={selectedAirline}
                        onChange={handleAirlineChange}
                        placeholder="Select airline"
                        isClearable
                        menuPortalTarget={document.body}
                        styles={selectStyles}
                      />
                    </div>
                    {flightInfo.flightCode && (
                      <div className="d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, borderRadius: 8, border: "1px solid #dfe3ec", background: "#fff", flexShrink: 0 }}>
                        <img width={22} height={22} alt={flightInfo.flightCode} src={`https://images.kiwi.com/airlines/32/${flightInfo.flightCode}.png`} style={{ objectFit: "contain" }} />
                      </div>
                    )}
                  </div>
                )}
                {field("Flight Number", true,
                  <div className="d-flex gap-2">
                    {textInput({ name: "flight_number", placeholder: "e.g. 101", value: flightInfo.flightNumber, onChange: handleFlightNumber })}
                    <button type="button" onClick={getFlightInfo} title="Fetch saved flight details"
                      style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 8, border: "none", background: GRAD, color: "#fff" }}>
                      <i className="bi bi-search" style={{ fontSize: 14 }} />
                    </button>
                  </div>
                )}
                {field("Departure Airport", true,
                  <AsyncSelect
                    cacheOptions
                    loadOptions={loadAirports}
                    defaultOptions={airportData}
                    value={depAirport}
                    onChange={handleDepAirportChange}
                    placeholder="Search departure airport"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Arrival Airport", true,
                  <AsyncSelect
                    cacheOptions
                    loadOptions={loadAirports}
                    defaultOptions={airportData}
                    value={arrAirport}
                    onChange={handleArrAirportChange}
                    placeholder="Search arrival airport"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Departure Time", true,
                  textInput({ name: "departure_time", placeholder: "HH:MM", value: inventoryInfo.departure_time, onChange: handleInventory }),
                  "col-12 col-md-3"
                )}
                {field("Arrival Time", true,
                  textInput({ name: "arrival_time", placeholder: "HH:MM", value: inventoryInfo.arrival_time, onChange: handleInventory }),
                  "col-12 col-md-3"
                )}
                {field("Dep. Terminal", false,
                  textInput({ name: "departure_terminal", placeholder: "e.g. T1", value: inventoryInfo.departure_terminal, onChange: handleInventory }),
                  "col-12 col-md-3"
                )}
                {field("Arr. Terminal", false,
                  textInput({ name: "arrival_terminal", placeholder: "e.g. T2", value: inventoryInfo.arrival_terminal, onChange: handleInventory }),
                  "col-12 col-md-3"
                )}
                {field("Next Day Arrival", false,
                  <Select
                    options={[{ value: "0", label: "No" }, { value: "1", label: "Yes" }]}
                    value={{ value: nextDayArrivalSelectedValue, label: nextDayArrivalSelectedValue === "1" ? "Yes" : "No" }}
                    onChange={(o) => setNextDayArrivalSelectedValue(o ? o.value : "0")}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />,
                  "col-12 col-md-3"
                )}
              </div>

              {/* Dynamic Stops */}
              {stopsData.map((stop, i) => (
                <div key={i} style={stopBox}>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <i className="bi bi-geo-alt" style={{ color: "#b45309" }}></i>
                    <span className="fw-bold" style={{ fontSize: 14, color: "#4b3f8f" }}>{numberSuffix(i + 1)} Stop</span>
                  </div>
                  <div className="row g-3">
                    {field("Departure Airport", true,
                      <AsyncSelect
                        cacheOptions loadOptions={loadAirports} defaultOptions={airportData}
                        value={stop.departure_airport}
                        onChange={(val) => handleStopChange(i, 'departure_airport', val)}
                        placeholder="Search departure airport" isClearable
                        menuPortalTarget={document.body} styles={selectStyles}
                      />
                    )}
                    {field("Arrival Airport", true,
                      <AsyncSelect
                        cacheOptions loadOptions={loadAirports} defaultOptions={airportData}
                        value={stop.arrival_airport}
                        onChange={(val) => handleStopChange(i, 'arrival_airport', val)}
                        placeholder="Search arrival airport" isClearable
                        menuPortalTarget={document.body} styles={selectStyles}
                      />
                    )}
                    {field("Departure Time", false,
                      textInput({ placeholder: "HH:MM", value: stop.departure_time, onChange: (e) => handleStopChange(i, 'departure_time', e.target.value) }),
                      "col-12 col-md-3"
                    )}
                    {field("Arrival Time", false,
                      textInput({ placeholder: "HH:MM", value: stop.arrival_time, onChange: (e) => handleStopChange(i, 'arrival_time', e.target.value) }),
                      "col-12 col-md-3"
                    )}
                    {field("Dep. Terminal", false,
                      textInput({ placeholder: "e.g. T1", value: stop.departure_terminal, onChange: (e) => handleStopChange(i, 'departure_terminal', e.target.value) }),
                      "col-12 col-md-3"
                    )}
                    {field("Arr. Terminal", false,
                      textInput({ placeholder: "e.g. T2", value: stop.arrival_terminal, onChange: (e) => handleStopChange(i, 'arrival_terminal', e.target.value) }),
                      "col-12 col-md-3"
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Rules Detail */}
            <div style={sectionCard}>
              {sectionTitle("bi-shield-check", "Rules Detail")}
              <div className="row g-3">
                {field("RBD", false,
                  textInput({ name: "rbd", placeholder: "Booking class, e.g. Y", maxLength: 1, value: inventoryInfo.rbd, onChange: handleInventory, style: { ...inputStyle, textTransform: "uppercase" } }),
                  "col-12 col-md-3"
                )}
                {field("Baggage Policy (kg)", false,
                  textInput({ name: "baggage_policy", placeholder: "kg", value: inventoryInfo.baggage_policy, onChange: handleInventory }),
                  "col-12 col-md-3"
                )}
                {field("Fare Rule", false,
                  <Select
                    options={STATUS_FARE_RULES}
                    value={STATUS_FARE_RULES.find(o => o.value === inventoryInfo.fare_rule) || null}
                    onChange={(o) => setInventoryInfo({ ...inventoryInfo, fare_rule: o ? o.value : "" })}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />,
                  "col-12 col-md-4"
                )}
                {field("Fare Code", false,
                  textInput({ name: "fare_code", placeholder: "e.g. ECO", maxLength: 3, value: inventoryInfo.fare_code, onChange: handleInventory, style: { ...inputStyle, textTransform: "uppercase" } }),
                  "col-12 col-md-2"
                )}
                {field("Supplier Type", false,
                  <Select
                    options={SUPPLIER_TYPES}
                    value={SUPPLIER_TYPES.find(o => o.value === inventoryInfo.supplier_type) || null}
                    onChange={(o) => handleInventory({ target: { name: "supplier_type", value: o ? o.value : "" } })}
                    placeholder="Select supplier type"
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />,
                  "col-12 col-md-3"
                )}
                {inventoryInfo.supplier_type === "other" && field("Supplier", true,
                  <AsyncSelect
                    cacheOptions defaultOptions
                    loadOptions={loadSuppliers}
                    value={selectedSupplier}
                    onChange={handleSelectChangeSupplier}
                    placeholder="Select supplier"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />,
                  "col-12 col-md-3"
                )}
                {field("Hide Inventory Before (Hr)", false,
                  textInput({ name: "hr", placeholder: "Hours", maxLength: 3, value: inventoryInfo.hr, onChange: handleInventory }),
                  "col-12 col-md-3"
                )}
              </div>
            </div>

            {/* PNR Details */}
            {dateValue.length >= 1 && (
              <div style={sectionCard}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  {sectionTitle("bi-ticket-perforated", "PNR Detail")}
                  {inventoryInfo.departure_airport_code && (
                    <span className="mb-3" style={{ fontSize: 12, fontWeight: 700, color: "#4b3f8f" }}>
                      {inventoryInfo.departure_airport_code} → {getLastStop()?.airport_code}
                    </span>
                  )}
                </div>
                {dateValue.map((singleDate, i) => (
                  <div key={i} style={{ ...stopBox, marginTop: i === 0 ? 0 : 16 }}>
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className="bi bi-calendar3" style={{ color: "#667eea" }}></i>
                      <span className="fw-bold" style={{ fontSize: 13, color: "#4b3f8f" }}>{singleDate.toString()}</span>
                      {nextDayArrivalSelectedValue === "1" && (
                        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "#fffbeb", color: "#b45309", fontWeight: 700 }}>+1 Day Arrival</span>
                      )}
                    </div>
                    <div className="row g-3">
                      {field("Departure Date", false,
                        textInput({ value: singleDate.toString(), readOnly: true, style: readOnlyStyle }), "col-12 col-md-3")}
                      {field("Arrival Date", false,
                        textInput({ value: nextDayArrivalSelectedValue === "0" ? singleDate.toString() : addDay(singleDate.toString(), 1), readOnly: true, style: readOnlyStyle }), "col-12 col-md-3")}
                      {field("PNR", true,
                        textInput({ placeholder: "e.g. ABC123", value: pnrInfo[i]?.pnr || "", onChange: (e) => handlePnrChange(i, 'pnr', e.target.value), style: { ...inputStyle, fontFamily: "monospace" } }), "col-12 col-md-2")}
                      {field("Seat Quantity", true,
                        <input type="number" min="0" className="form-control" style={inputStyle} value={pnrInfo[i]?.seat_quantity ?? 0} onChange={(e) => handlePnrChange(i, 'seat_quantity', e.target.value)} />, "col-12 col-md-2")}
                      {field("Infant Amount", false,
                        <input type="number" min="0" step="0.01" className="form-control" style={inputStyle} value={pnrInfo[i]?.infant_amount ?? 0} onChange={(e) => handlePnrChange(i, 'infant_amount', e.target.value)} />, "col-12 col-md-2")}
                      {[["basic_amount", "Basic Amount"], ["yq_amount", "YQ Amount"], ["yr_amount", "YR Amount"], ["ot_amount", "OT Amount"]].map(([key, label]) => (
                        <React.Fragment key={key}>
                          {field(label, false,
                            <input type="number" min="0" step="0.01" className="form-control" style={inputStyle} value={pnrInfo[i]?.[key] ?? 0} onChange={(e) => handlePnrChange(i, key, e.target.value)} />, "col-12 col-md-3")}
                        </React.Fragment>
                      ))}
                      {field("Total Amount (₹)", false,
                        <input type="number" min="0" className="form-control" style={{ ...readOnlyStyle, fontWeight: 700 }} value={pnrInfo[i]?.total_amount ?? 0} readOnly />, "col-12 col-md-4")}
                      {field("Markup 1", false,
                        <input type="number" min="0" step="0.01" className="form-control" style={inputStyle} value={pnrInfo[i]?.markup1 ?? 0} onChange={(e) => handlePnrChange(i, 'markup1', e.target.value)} />, "col-12 col-md-4")}
                      {field("Markup 2", false,
                        <input type="number" min="0" step="0.01" className="form-control" style={inputStyle} value={pnrInfo[i]?.markup2 ?? 0} onChange={(e) => handlePnrChange(i, 'markup2', e.target.value)} />, "col-12 col-md-4")}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2">
              <Link to="/inventory/list" className="btn px-4 d-flex align-items-center"
                style={{ background: "#fff", border: "1px solid #dfe3ec", borderRadius: 8, height: 40, fontSize: 13, color: "#444" }}>
                Back
              </Link>
              <button type="submit" className="btn px-4 text-white fw-semibold"
                style={{ background: GRAD, border: "none", borderRadius: 8, height: 40, fontSize: 13 }}>
                Add Inventory
              </button>
            </div>

        </form>
      </section>
    </>
  )
}

export default AddInventory
